import React, { useState, useEffect } from 'react';

const CAMarkView = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [caMarks, setCaMarks] = useState(null);
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Mock data for current semester CA marks
  useEffect(() => {
    setTimeout(() => {
      setCaMarks({
        semester: "2nd Semester 2023/2024",
        subjects: [
          { 
            code: "CS201", 
            name: "Data Structures", 
            caMark: 82, 
            maxMark: 100,
            lastUpdated: "Mar 15, 2024",
            assignments: [
              { title: "Assignment 1", mark: 18, max: 20, weight: 20 },
              { title: "Assignment 2", mark: 20, max: 20, weight: 20 },
              { title: "Quiz 1", mark: 15, max: 15, weight: 15 },
              { title: "Lab Test", mark: 29, max: 30, weight: 30 }
            ]
          },
          { 
            code: "CS202", 
            name: "Web Development", 
            caMark: 90, 
            maxMark: 100,
            lastUpdated: "Mar 18, 2024",
            assignments: [
              { title: "HTML/CSS Project", mark: 45, max: 50, weight: 50 },
              { title: "JavaScript Quiz", mark: 18, max: 20, weight: 20 },
              { title: "React Assignment", mark: 27, max: 30, weight: 30 }
            ]
          },
          { 
            code: "MATH201", 
            name: "Statistics", 
            caMark: 75, 
            maxMark: 100,
            lastUpdated: "Mar 10, 2024",
            assignments: [
              { title: "Problem Set 1", mark: 25, max: 30, weight: 30 },
              { title: "Midterm Test", mark: 50, max: 70, weight: 70 }
            ]
          }
        ],
        totalAverage: 82.3,
        lastUpdated: "Mar 20, 2024"
      });
      setIsLoading(false);
    }, 800);
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-lg text-gray-600">Loading your current CA marks...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Current Semester CA Marks</h1>
          <div className="flex flex-wrap justify-center gap-4">
            <span className="px-4 py-1 bg-teal-500 text-white rounded-full text-sm font-medium">
              {caMarks.semester}
            </span>
            <span className="text-gray-500 text-sm">
              Last updated: {caMarks.lastUpdated}
            </span>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Average Card */}
          <div className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center transition-all duration-300 hover:shadow-lg">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Current CA Average</h3>
            <div className="relative w-32 h-32 mb-4">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#e2e8f0"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray="251.2"
                  strokeDashoffset={251.2 - (251.2 * caMarks.totalAverage / 100)}
                  transform="rotate(-90 50 50)"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold text-gray-800">
                  {caMarks.totalAverage}%
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-500">Based on all subjects</p>
          </div>

          {/* Subjects Card */}
          <div className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center justify-center transition-all duration-300 hover:shadow-lg">
            <h3 className="text-lg font-medium text-gray-700 mb-2">Subjects</h3>
            <p className="text-5xl font-bold text-teal-500 my-4">
              {caMarks.subjects.length}
            </p>
            <p className="text-sm text-gray-500">In current semester</p>
          </div>

          {/* Date Card */}
          <div className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center justify-center transition-all duration-300 hover:shadow-lg">
            <h3 className="text-lg font-medium text-gray-700 mb-2">Report Date</h3>
            <p className="text-2xl font-bold text-gray-800 my-4">
              {currentDate}
            </p>
            <p className="text-sm text-gray-500">Current date</p>
          </div>
        </div>

        {/* Subjects List */}
        <div className="space-y-6">
          {caMarks.subjects.map((subject, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg"
            >
              {/* Subject Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="mb-4 md:mb-0">
                    <h2 className="text-xl font-bold text-gray-800">
                      {subject.name} <span className="text-gray-500">({subject.code})</span>
                    </h2>
                    <p className="text-sm text-gray-500">
                      Last updated: {subject.lastUpdated}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <div className="text-right mr-4">
                      <span className="block text-2xl font-bold text-gray-800">
                        {subject.caMark}/{subject.maxMark}
                      </span>
                      <span className="text-sm text-gray-500">
                        {Math.round((subject.caMark/subject.maxMark)*100)}%
                      </span>
                    </div>
                    <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-teal-500 transition-all duration-500" 
                        style={{ width: `${(subject.caMark/subject.maxMark)*100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Assignments Breakdown */}
              <div className="p-6">
                <h4 className="text-lg font-medium text-gray-700 mb-4">Assignment Breakdown</h4>
                <div className="space-y-4">
                  {subject.assignments.map((assignment, idx) => (
                    <div key={idx} className="flex flex-col md:flex-row md:items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="mb-2 md:mb-0">
                        <span className="font-medium text-gray-700">{assignment.title}</span>
                        <span className="ml-2 text-sm text-gray-500">({assignment.weight}%)</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-800 mr-3">
                          {assignment.mark}/{assignment.max}
                        </span>
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500 transition-all duration-500" 
                            style={{ width: `${(assignment.mark/assignment.max)*100}%` }}
                          ></div>
                        </div>
                        <span className="ml-2 text-sm font-medium text-gray-600">
                          ({Math.round((assignment.mark/assignment.max)*100)}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>              
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CAMarkView;